import { useAuth0 } from '@auth0/auth0-react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import InputAdornment from '@material-ui/core/InputAdornment';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Typography from '@material-ui/core/Typography';
import { Form, Formik } from 'formik';
import { compile } from 'path-to-regexp';
import * as R from 'ramda';
import React from 'react';
import * as Yup from 'yup';
import useProfileStorage from '../../hooks/useProfileStorage';
import useTechSkills from '../../hooks/useTechSkills';
import routes from '../../routes';
import {
  CountryField,
  FormButtonsContainer,
  SubmitButton,
  TextField,
  ToggableChipsField,
} from '../Fields';
import LoadingPage from '../Loading/Page';
import {
  TypistAfterFinish, TypistDelay, TypistDelayLength, TypistItem, TypistWrapper,
} from '../Typist';

type SlideProps = {
  onNext: () => void;
};

const useStyles = makeStyles((theme) => ({
  nextSlideButton: {
    marginTop: theme.spacing(2),
    display: 'block',
  },
  slideTextBox: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    margin: '0 5vw',
    '& > div': {
      borderLeft: '5px solid black',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'flex-start',
      paddingLeft: 20,
    },
    '& > div > p': {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    '& .first': {
      fontSize: 'xx-large',
      marginBottom: theme.spacing(1),
    },
    '& .second': {
      fontSize: 'large',
      marginBottom: theme.spacing(1),
    },
    '& .third': {
      fontSize: 'x-large',
      marginBottom: theme.spacing(1),
    },
  },
  profileSlide: {
    flexDirection: 'column-reverse',
    flex: 1,
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'flex-end',
    '& > *': {
      flex: 'initial',
      margin: theme.spacing(2),
    },
    '@media (min-width: 760px)': {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      '& > *': {
        flex: 1,
      },
    },
  },
  profileBuilder: {
    marginTop: theme.spacing(2),
  },
  form: {
    marginTop: theme.spacing(2),
    width: '100%',
    '& > *': {
      marginBottom: theme.spacing(1),
    },
  },
  signupButton: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    '& > *': {
      marginTop: theme.spacing(2),
    },
  },
}));

export function Slide1({ onNext }: SlideProps) {
  const classes = useStyles();
  return (
    <Box className={classes.slideTextBox}>
      <div>
        <TypistWrapper>
          <TypistItem className="first">
            Hello there!
            <TypistDelay length={TypistDelayLength.normal} />
            {' '}
            Welcome to Matilda&apos;s compensation report.
            <TypistDelay length={TypistDelayLength.long} />
          </TypistItem>
          <TypistItem className="second">
            Our goal with this report is to bring more transparency to engineer compensation.
            {' '}
            <TypistDelay length={TypistDelayLength.long} />
            We will anonymize, tabulate and analyze your responses to build this report.
            {' '}
            <TypistDelay length={TypistDelayLength.short} />
            Just 6 simple and quick questions and you&apos;ll be done.
          </TypistItem>
          <TypistAfterFinish>
            <Button variant="contained" color="primary" autoFocus onClick={onNext} className={classes.nextSlideButton}>
              Let&apos; get started
            </Button>
          </TypistAfterFinish>
        </TypistWrapper>
      </div>
    </Box>
  );
}

const Slide2Schema = Yup.object().shape({
  role: Yup.string()
    .required('Please fill in your current role in your company'),
});

export function Slide2({ onNext }: SlideProps) {
  const classes = useStyles();
  const { profile, fetchingProfile, writeProfile } = useProfileStorage();
  if (fetchingProfile || !profile) return <LoadingPage />;
  return (
    <Box className={classes.slideTextBox}>
      <div>
        <TypistWrapper interDelay={TypistDelayLength.normal} endDelay>
          <TypistItem className="second">
            What is your current job&apos;s role?
          </TypistItem>
          <TypistAfterFinish>
            <Formik
              initialValues={profile}
              validationSchema={Slide2Schema}
              onSubmit={(values) => {
                writeProfile(values);
                onNext();
              }}
            >
              <Form className={classes.form}>
                <TextField name="role" autoFocus placeholder="E.g.: Senior Frontend Engineer" />
                <FormButtonsContainer>
                  <SubmitButton text="Next" />
                </FormButtonsContainer>
              </Form>
            </Formik>
          </TypistAfterFinish>
        </TypistWrapper>
      </div>
    </Box>
  );
}

const Slide3Schema = Yup.object().shape({
  yearsOfExperience: Yup.number()
    .required('Please fill in your years of experience'),
});

export function Slide3({ onNext }: SlideProps) {
  const classes = useStyles();
  const { profile, fetchingProfile, writeProfile } = useProfileStorage();
  if (fetchingProfile || !profile) return <LoadingPage />;
  return (
    <Box className={classes.slideTextBox}>
      <div>
        <TypistWrapper interDelay={TypistDelayLength.normal} endDelay>
          <TypistItem className="second">
            What is your experience in terms of
            {' '}
            <strong>years</strong>
            {' '}
            working professionally in
            {' '}
            <strong>Software Engineering?</strong>
          </TypistItem>
          <TypistAfterFinish>
            <Formik
              initialValues={profile}
              validationSchema={Slide3Schema}
              onSubmit={(values) => {
                writeProfile(values);
                onNext();
              }}
            >
              <Form className={classes.form}>
                <TextField
                  name="yearsOfExperience"
                  autoFocus
                  type="number"
                  InputProps={{
                    endAdornment: <InputAdornment position="start">year(s)</InputAdornment>,
                  }}
                />
                <FormButtonsContainer>
                  <SubmitButton text="Next" />
                </FormButtonsContainer>
              </Form>
            </Formik>
          </TypistAfterFinish>
        </TypistWrapper>
      </div>
    </Box>
  );
}

export const toWelcome4Path = compile(routes.steps.withPage);

const skillsSchema = Yup.object().shape({
  skills: Yup.array().min(0),
});

export function Slide4({ onNext }: SlideProps) {
  const classes = useStyles();
  const { profile, writeProfile, fetchingProfile } = useProfileStorage();
  const { skills, fetchingSkills } = useTechSkills();
  const loading = fetchingProfile || !profile || fetchingSkills;
  if (loading) return <LoadingPage />;
  const selectedSkills = profile.skills;
  const selectedSkillsAvailable = R.intersection(selectedSkills, skills || []);
  const initialValues = {
    ...profile,
    skills: selectedSkillsAvailable,
  };
  return (
    <Box className={classes.slideTextBox}>
      <div>
        <TypistWrapper interDelay={TypistDelayLength.normal} endDelay>
          <TypistItem className="second" key="noneOfSkills">
            Which of these technologies are relevant
            {' '}
            <strong>in your current role</strong>
            ?
          </TypistItem>
          <TypistAfterFinish>
            <Formik
              initialValues={initialValues}
              enableReinitialize
              validationSchema={skillsSchema}
              onSubmit={(values) => {
                writeProfile(values);
                onNext();
              }}
            >
              {({ errors }) => (
                <Form className={classes.form}>
                  <ToggableChipsField
                    name="skills"
                    options={skills || []}
                    getOptionLabel={(skill) => skill.name}
                    autoFocus
                  />
                  <FormButtonsContainer>
                    {errors.skills
                      ? <Typography color="secondary">{errors.skills}</Typography>
                      : null}
                    <SubmitButton text="Next" />
                  </FormButtonsContainer>
                </Form>
              )}
            </Formik>
          </TypistAfterFinish>
        </TypistWrapper>
      </div>
    </Box>
  );
}

const Slide5Schema = Yup.object().shape({
  country: Yup.string()
    .nullable()
    .required('Please select a country'),
});

export function Slide5({ onNext }: SlideProps) {
  const classes = useStyles();
  const { profile, fetchingProfile, writeProfile } = useProfileStorage();
  if (fetchingProfile || !profile) return <LoadingPage />;
  return (
    <Box className={classes.slideTextBox}>
      <div>
        <TypistWrapper interDelay={TypistDelayLength.normal} endDelay>
          <TypistItem className="second">
            What is the country you are
            {' '}
            <strong>currently living</strong>
            {' '}
            in?
          </TypistItem>
          <TypistAfterFinish>
            <Formik
              initialValues={profile}
              validationSchema={Slide5Schema}
              onSubmit={(values) => {
                writeProfile(values);
                onNext();
              }}
            >
              <Form className={classes.form}>
                <CountryField name="country" autoFocus />
                <FormButtonsContainer>
                  <SubmitButton text="Next" />
                </FormButtonsContainer>
              </Form>
            </Formik>
          </TypistAfterFinish>
        </TypistWrapper>
      </div>
    </Box>
  );
}

const Slide6Schema = Yup.object().shape({
  company: Yup.object().shape({
    country: Yup.string()
      .nullable()
      .required('Please select a country'),
    // name: Yup.string().nullable(),
    // numberOfEmployees: Yup.number().typeError('Please write a number')
    // .min(1, 'At least one person, right?').nullable(),
    // createdAtYear: Yup.number()
    //   .typeError('Please write a valid year')
    //   .min(1000, 'Sorry, we only support the current millennia')
    //   .max(new Date().getUTCFullYear(),
    // 'We don\t yet support the future. Please try again in a couple of years')
    //   .nullable(),
    // financingAmount: Yup.number().min(0).nullable(),
  }),
});

export function Slide6({ onNext }: SlideProps) {
  const classes = useStyles();
  const { profile, fetchingProfile, writeProfile } = useProfileStorage();
  if (fetchingProfile || !profile) return <LoadingPage />;
  return (
    <Box className={classes.slideTextBox}>
      <div>
        <TypistWrapper interDelay={TypistDelayLength.normal} endDelay>
          <TypistItem className="second">
            {/* Tell us about your current job&apos;s company */}
            Where are the headquarters
            {' '}
            <strong>of the company you work for</strong>
            ?
          </TypistItem>
          <TypistAfterFinish>
            <Formik
              initialValues={profile}
              validationSchema={Slide6Schema}
              onSubmit={(values) => {
                writeProfile(values);
                onNext();
              }}
            >
              <Form className={classes.form}>
                <CountryField
                  name="company.country"
                  label="Country"
                  autoFocus
                  required
                  helperText="Where the main offices of the company are located"
                />
                {/* <TextField name="company.name" label="Name" fullWidth helperText="Optional." />
                <TextField name="company.numberOfEmployees" type="number" label="Number of employees
                " fullWidth helperText="Optional. It doesn't need to be precise." />
                <TextField name="company.createdAtYear" type="number" label="Year it was created"
                fullWidth helperText="Optional. It doesn't need to be precise" />
                <TextField
                  name="company.financingAmount"
                  type="number"
                  label="How much financing"
                  fullWidth
                  helperText="Optional. It doesn't need to be precise"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">USD $</InputAdornment>,
                  }}
                /> */}
                <FormButtonsContainer>
                  <SubmitButton text="Next" />
                </FormButtonsContainer>
              </Form>
            </Formik>
          </TypistAfterFinish>
        </TypistWrapper>
      </div>
    </Box>
  );
}

const Slide7Schema = Yup.object().shape({
  grossMonthlyCompensation: Yup.number()
    .required('Please fill in your currently salary'),
});

export function Slide7({ onNext }: SlideProps) {
  const classes = useStyles();
  const { profile, fetchingProfile, writeProfile } = useProfileStorage();
  if (fetchingProfile || !profile) return <LoadingPage />;
  return (
    <Box className={classes.slideTextBox}>
      <div>
        <TypistWrapper interDelay={TypistDelayLength.normal} endDelay>
          <TypistItem className="second">
            What is your current
            {' '}
            <strong>gross monthly</strong>
            {' '}
            salary in
            {' '}
            <strong>US dollars</strong>
            {' '}
            (USD)?
          </TypistItem>
          <TypistAfterFinish>
            <>
              <Typography variant="body2" color="textSecondary">
                This is the amount the company pays you before deductions like taxes,
                health insurance and other type of discounts.
                Also known as &quot;salario bruto&quot;
                in Spanish and &quot;salário bruto&quot; in Portuguese.
              </Typography>
              <Formik
                initialValues={profile}
                validationSchema={Slide7Schema}
                onSubmit={(values) => {
                  writeProfile(values);
                  onNext();
                }}
              >
                <Form className={classes.form}>
                  <TextField
                    name="grossMonthlyCompensation"
                    autoFocus
                    type="number"
                    helperText="The one before taxes and other discounts, in US dollars"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">USD $</InputAdornment>,
                    }}
                  />
                  <FormButtonsContainer>
                    <SubmitButton text="Next" />
                  </FormButtonsContainer>
                </Form>
              </Formik>
            </>
          </TypistAfterFinish>
        </TypistWrapper>
      </div>
    </Box>
  );
}

export function Slide8() {
  const classes = useStyles();
  const { loginWithRedirect } = useAuth0();
  function handleClick() {
    loginWithRedirect({ redirectUri: `${window.location.origin}${routes.steps.submitProfile}` });
  }
  return (
    <Box className={classes.slideTextBox}>
      <div>
        <TypistWrapper interDelay={TypistDelayLength.normal}>
          <TypistItem className="second">
            Verify your email so we can send you back the report as soon as it&apos;s ready
          </TypistItem>
          <TypistAfterFinish>
            <Box className={classes.signupButton}>
              <Button
                type="button"
                variant="contained"
                color="primary"
                onClick={handleClick}
                autoFocus
              >
                Validate e-mail
              </Button>
            </Box>
          </TypistAfterFinish>
        </TypistWrapper>
      </div>
    </Box>
  );
}
